import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Mic, Clock, Music } from 'lucide-react';
import { QuestionnaireData } from '@/types/category';
import VoiceGenderSelection from '../VoiceGenderSelection';
import BackgroundMusicSelection from '../BackgroundMusicSelection';

interface VoicePreferencesStepProps {
  formData: Partial<QuestionnaireData>;
  onUpdate: (updates: Partial<QuestionnaireData>) => void;
  categoryId: string;
  freeSessionsAvailable?: number;
}

const SESSION_LENGTHS = [
  { value: 180, label: '3 minutes', credits: 1 },
  { value: 300, label: '5 minutes', credits: 2 },
  { value: 420, label: '7 minutes', credits: 3 },
  { value: 600, label: '10 minutes', credits: 4 },
  { value: 900, label: '15 minutes', credits: 6 },
  { value: 1200, label: '20 minutes', credits: 8 },
  { value: 1800, label: '30 minutes', credits: 12 }
];

export const VoicePreferencesStep: React.FC<VoicePreferencesStepProps> = ({
  formData,
  onUpdate,
  categoryId,
  freeSessionsAvailable = 0
}) => {
  const handleSessionLengthChange = (value: string) => {
    // Store as string in the expected format "X minutes"
    const lengthData = SESSION_LENGTHS.find(l => l.value === parseInt(value));
    if (lengthData) {
      onUpdate({ sessionLength: lengthData.label });
    }
  };

  const handleBackgroundSoundsToggle = (checked: boolean) => {
    onUpdate({
      backgroundSounds: checked,
      includeBackgroundSounds: checked
    });
  };

  // Handle sessionLength as either string ("X minutes") or number (seconds)
  const selectedLength = typeof formData.sessionLength === 'string'
    ? SESSION_LENGTHS.find(l => l.label === formData.sessionLength)
    : SESSION_LENGTHS.find(l => l.value === formData.sessionLength);
  const creditCost = selectedLength?.credits || 1;
  const isFree = freeSessionsAvailable > 0;

  return (
    <div className="space-y-6">
      {/* Step Header */}
      <div className="text-center mb-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
          <Mic className="h-6 w-6 text-primary" />
        </div>
        <h3 className="text-lg font-semibold mb-1">Voice & Audio Preferences</h3>
        <p className="text-sm text-muted-foreground">
          Choose your preferred voice and session length
        </p>
      </div>

      {/* Session Length */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Session Length <span className="text-destructive">*</span>
            </Label>
            <Select
              value={String(selectedLength?.value || 180)}
              onValueChange={handleSessionLengthChange}
            >
              <SelectTrigger className="rounded-xl">
                <SelectValue placeholder="Select session length" />
              </SelectTrigger>
              <SelectContent>
                {SESSION_LENGTHS.map(length => (
                  <SelectItem key={length.value} value={String(length.value)}>
                    <div className="flex items-center justify-between w-full">
                      <span>{length.label}</span>
                      <span className="ml-4 text-xs text-muted-foreground">
                        {isFree && creditCost <= freeSessionsAvailable
                          ? 'Free'
                          : `${length.credits} credits`
                        }
                      </span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedLength && (
              <p className="text-xs text-muted-foreground">
                {isFree && creditCost <= freeSessionsAvailable
                  ? `Using ${creditCost} of your ${freeSessionsAvailable} free session${freeSessionsAvailable > 1 ? 's' : ''}`
                  : `This session will cost ${creditCost} credit${creditCost > 1 ? 's' : ''}`
                }
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Voice Selection */}
      <VoiceGenderSelection
        formData={formData}
        setFormData={onUpdate}
        categoryId={categoryId}
      />

      {/* Background Sounds Toggle */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Label htmlFor="background-sounds" className="text-sm font-medium flex items-center gap-2">
                <Music className="h-4 w-4" />
                Include Background Music
              </Label>
              <p className="text-xs text-muted-foreground">
                Add background music to enhance relaxation
              </p>
            </div>
            <Switch
              id="background-sounds"
              checked={formData.backgroundSounds ?? true}
              onCheckedChange={handleBackgroundSoundsToggle}
            />
          </div>
        </CardContent>
      </Card>

      {/* Background Music Selection - only shown when background sounds enabled */}
      {(formData.backgroundSounds ?? true) && (
        <BackgroundMusicSelection
          formData={formData}
          setFormData={onUpdate}
          categoryId={categoryId}
        />
      )}

      {/* Credit Cost Summary */}
      {selectedLength && (
        <Card className={isFree && creditCost <= freeSessionsAvailable
          ? "border-emerald-200 bg-gradient-to-r from-white to-emerald-50/30"
          : "border-amber-200 bg-gradient-to-r from-white to-amber-50/30"
        }>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm font-medium">Session Cost</p>
                <p className="text-xs text-muted-foreground">
                  {selectedLength.label} session
                </p>
              </div>
              <div className="text-right">
                {isFree && creditCost <= freeSessionsAvailable ? (
                  <>
                    <p className="text-sm font-semibold text-emerald-600">Free Session</p>
                    <p className="text-xs text-muted-foreground">
                      {freeSessionsAvailable - creditCost} free remaining
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-semibold">{creditCost} credit{creditCost > 1 ? 's' : ''}</p>
                    <p className="text-xs text-muted-foreground">
                      Will be deducted on creation
                    </p>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};